import { type Profile, isControlled, profileRank } from './profiles';

export const ROLES = [
  'product_owner',
  'project_manager',
  'security_champion',
  'tester_qa',
  'architect',
  'business_analyst',
  'devsecops',
  'dpo',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  product_owner: 'Product Owner',
  project_manager: 'Project Manager',
  security_champion: 'Security Champion',
  tester_qa: 'Tester / QA',
  architect: 'Architect',
  business_analyst: 'Business Analyst',
  devsecops: 'DevSecOps Engineer',
  dpo: 'Data Protection Officer',
};

export const isRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).includes(value);

export type RoleException = {
  person: string;
  role: Role;
  /** The SSD 7.1 exception reference, e.g. SSD-EX-2026-041. */
  reference: string;
  /** ISO date; an expired exception no longer applies. */
  expires?: string;
};

/** `.vivicta/asdp/roles.yaml` (journey spec §7.3). */
export type RolesFile = {
  schema_version: 1;
  /** Phase 1 keeps the profile here until the gate App sets the custom property. */
  profile?: Profile;
  roles: Partial<Record<Role, string[]>>;
  exceptions?: RoleException[];
};

const CORE_ROLES: readonly Role[] = [
  'product_owner',
  'project_manager',
  'security_champion',
  'tester_qa',
  // Gate 3 needs one; below P2 a named technical lead can hold it (§3.2).
  'architect',
];
const CONTROLLED_ROLES: readonly Role[] = [
  'business_analyst',
  'devsecops',
];

/** Roles that must be named at Gate 1 (journey spec §4.3). */
export function requiredRoles(
  profile: Profile,
  personalData: boolean,
): Role[] {
  return [
    ...CORE_ROLES,
    ...(isControlled(profile) ? CONTROLLED_ROLES : []),
    ...(personalData ? (['dpo'] as const) : []),
  ];
}

export type Severity = 'must-not' | 'should-not';

/**
 * Role combinations from ASDP §2.5 (ASDP-SOD-03) for the roles the portal
 * tracks; a "must not" the team can't meet is an SSD 7.1 exception. Rules
 * about one change (author and approver, Developer and Tester / QA) are
 * enforced per pull request, not here. `from` is the lowest profile where the
 * rule applies.
 */
export const ROLE_COMBINATION_RULES: ReadonlyArray<{
  roles: readonly [Role, Role];
  severity: Severity;
  from: Profile;
  why: string;
}> = [
  {
    roles: ['product_owner', 'security_champion'],
    severity: 'must-not',
    from: 'P0',
    // SSD 3.2: two people approve a production deployment.
    why: 'Releasing to production needs two different people to approve',
  },
  {
    roles: ['architect', 'security_champion'],
    severity: 'must-not',
    from: 'P3',
    why: 'The design needs a security review by someone other than its designer',
  },
  {
    roles: ['architect', 'security_champion'],
    severity: 'should-not',
    from: 'P1',
    why: "A security review is stronger when it's done by someone other than the designer",
  },
  {
    roles: ['product_owner', 'project_manager'],
    severity: 'should-not',
    from: 'P3',
    why: 'Someone other than the Project Manager should decide the classification they propose',
  },
];

export type RoleWarning = {
  person: string;
  roles: [Role, Role];
  severity: Severity;
  /** Whether it holds the user up: not allowed, or allowed with a reason. */
  headline: string;
  message: string;
};

/** "anna can be both Architect and Security Champion": what a reason for a combination explains. */
export const canHoldBoth = (warning: Pick<RoleWarning, 'person' | 'roles'>) =>
  `${warning.person} can be both ${ROLE_LABELS[warning.roles[0]]} and ${ROLE_LABELS[warning.roles[1]]}`;

export const sameLogin = (a: string, b: string) =>
  a.replace(/^@/, '').toLowerCase() === b.replace(/^@/, '').toLowerCase();

const exceptionCovers = (
  exceptions: readonly RoleException[],
  person: string,
  roles: readonly Role[],
  today: string,
) =>
  exceptions.some(
    exception =>
      sameLogin(exception.person, person) &&
      roles.includes(exception.role) &&
      (!exception.expires || exception.expires >= today),
  );

/**
 * Combination warnings for people holding two roles. Neither blocks, so one
 * person can try the flow: "must-not" warns, "should-not" asks for a reason.
 * A recorded, unexpired exception clears either.
 */
export function roleWarnings(
  roles: RolesFile['roles'],
  profile: Profile,
  exceptions: readonly RoleException[] = [],
  today = new Date().toISOString().slice(0, 10),
): RoleWarning[] {
  const holders = (role: Role) => roles[role] ?? [];
  const warnings: RoleWarning[] = [];
  const seen = new Set<string>();
  for (const rule of ROLE_COMBINATION_RULES) {
    if (profileRank(profile) < profileRank(rule.from)) continue;
    const [a, b] = rule.roles;
    for (const person of holders(a)) {
      const key = `${person.toLowerCase()}|${[a, b].sort().join('+')}`;
      if (
        seen.has(key) ||
        !holders(b).some(other => sameLogin(other, person)) ||
        exceptionCovers(exceptions, person, rule.roles, today)
      ) {
        continue;
      }
      seen.add(key);
      warnings.push({
        person,
        roles: [a, b],
        severity: rule.severity,
        headline:
          rule.severity === 'must-not'
            ? `Not allowed at ${profile}. You can carry on for now.`
            : 'Better to split, but you can keep it if you say why.',
        message: `${person} is both ${ROLE_LABELS[a]} and ${ROLE_LABELS[b]}. ${rule.why}${
          rule.severity === 'must-not'
            ? ', so before real work give one of these roles to someone else, or record an approved exception.'
            : '.'
        }`,
      });
    }
  }
  return warnings;
}

export const missingRoles = (
  roles: RolesFile['roles'],
  profile: Profile,
  personalData: boolean,
) =>
  requiredRoles(profile, personalData).filter(
    role => (roles[role] ?? []).length === 0,
  );

export const rolesOf = (roles: RolesFile['roles'], login: string): Role[] =>
  ROLES.filter(role =>
    (roles[role] ?? []).some(holder => sameLogin(holder, login)),
  );
